import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPoint,
  PdfRect,
  PdfBrush,
  PdfColor,
  ETextAlign,
  PageGen,
  PdfFont,
} from '../../pdf/pdf';
import { ILayoutMultiGen } from '../layoutMulti';
import { OptsMulti } from '../optsMulti';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin, genMulti, fixGenArray } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/transforms';
import '../../pdf/draw/helpers';

import { IItemCount } from '@educraft/base';

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

function drawRainbowRect(page: PageGen, rect: PdfRect, stripes = 60): void {
  const stripeW = rect.width / stripes;
  for (let i = 0; i < stripes; i++) {
    const hue = (i / stripes) * 360; // one rainbow cycle
    const { r, g, b } = hslToRgb(hue, 1, 0.85); // Light rainbow
    const color = PdfColor.fromRgb(r, g, b);
    const stripeRect = new PdfRect(rect.x + i * stripeW, rect.y, stripeW + 1, rect.height);
    page.drawRectangle(null, new PdfBrush(color), stripeRect);
  }
}

/**
 * Shared base class for Kolickovani variants
 */
abstract class KolickovaniBase implements ILayoutMultiGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  abstract generate(pdf: PdfGen, opts: OptsMulti): Promise<void>;
}

/**
 * Kolickovani__1 (Peg Board)
 * Layout: Landscape pages, 5x2 grid. Rotated answers at bottom.
 */
export class Kolickovani__1 extends KolickovaniBase {
  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const countX = 5;
    const countY = 2;
    const itemsPerPage = countX * countY;
    const totalCount = this.countDefault();

    const { origin, size } = calcOrigin(countX, countY, margin, PdfSize.A4H);

    for (let i = 0; i < totalCount; i += itemsPerPage) {
      const page = pdf.addPage(true);
      const count = Math.min(itemsPerPage, totalCount - i);
      const gen = genMulti(opts.data, opts.generator, count, false, false);

      const allTexts = gen.map((g) => fixGenArray(g.texts));
      const questionFont = page.adjustFontMulti(allTexts, GUtil.baseFont, size);

      for (let j = 0; j < gen.length; j++) {
        const x = j % countX;
        const y = Math.floor(j / countX);
        const q = gen[j];
        const optionHeight = PdfUnit.FromMillimeters(50);
        const labelFont = page.adjustFont(optionHeight, q.answers, GUtil.baseFont);

        const rect = new PdfRect(
          origin.x + x * size.width,
          origin.y + y * size.height,
          size.width,
          size.height
        );
        const r1 = new PdfRect(rect.x, rect.y, rect.width, rect.height - optionHeight);

        page.drawRectangle(GUtil.basePen, null, rect);
        page.drawRectangle(GUtil.basePen, null, r1);
        page.drawStringsMulti(
          fixGenArray(q.texts),
          questionFont,
          PdfBrush.Black,
          null,
          r1,
          ETextAlign.Center
        );

        const optionWidth = size.width / q.answers.length;
        for (let k = 0; k < q.answers.length; k++) {
          const rr = new PdfRect(r1.x + k * optionWidth, r1.bottom, optionWidth, optionHeight);
          page.drawRectangle(GUtil.basePen, null, rr);
          page.save();
          page.rotateAtTransform(270, new PdfPoint(rr.centerX, rr.centerY));
          page.drawString(q.answers[k], labelFont, PdfBrush.Black, null, rr, ETextAlign.Center);
          page.restore();
        }
      }
    }
  }
}

/**
 * Kolickovani__2 (Peg Board - Variant 2)
 * Layout: Portrait pages, 3x3 grid. Rounded cards, colored background.
 */
export class Kolickovani__2 extends KolickovaniBase {
  private static readonly allColors: string[][] = [
    [
      '#5995a7',
      '#649dae',
      '#6fa4b5',
      '#79acbc',
      '#84b4c3',
      '#8fbccb',
      '#99c3d2',
      '#a4cbd9',
      '#afd3e0',
    ],
    [
      '#0a7793',
      '#208790',
      '#32988d',
      '#43a889',
      '#53b883',
      '#63c87b',
      '#74d971',
      '#84e964',
      '#94f950',
    ],
    [
      '#7afb8f',
      '#5ae66b',
      '#32d143',
      '#53c944',
      '#a6d27c',
      '#e7d9ac',
      '#f2d9ce',
      '#dcd4e5',
      '#c6cefb',
    ],
    [
      '#4db4db',
      '#7588df',
      '#5b56c3',
      '#361a9a',
      '#3c33a6',
      '#4752b7',
      '#5091c5',
      '#6bc5d4',
      '#aae6f0',
    ],
  ];

  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const countX = 3;
    const countY = 3;
    const itemsPerPage = countX * countY;
    const totalCount = this.countDefault();
    const margin = PdfUnit.FromMillimeters(5);
    const margin2 = PdfUnit.FromMillimeters(3);
    const { origin, size } = calcOrigin(countX, countY, margin, PdfSize.A4);
    const colors = GUtil.randomBrushes(opts.data.random, Kolickovani__2.allColors);

    for (let i = 0; i < totalCount; i += itemsPerPage) {
      const page = pdf.addPage();
      const count = Math.min(itemsPerPage, totalCount - i);
      const gen = genMulti(opts.data, opts.generator, count, false, false);

      const allTexts = gen.map((g) => fixGenArray(g.texts));
      const questionFont = page.adjustFontMulti(
        allTexts,
        GUtil.baseFont,
        new PdfSize(size.width - 2 * margin2, size.height / 2 - 2 * margin2)
      );

      for (let j = 0; j < gen.length; j++) {
        const x = j % countX;
        const y = Math.floor(j / countX);
        const q = gen[j];
        const rect = new PdfRect(
          origin.x + x * size.width,
          origin.y + y * size.height,
          size.width,
          size.height
        );
        const color = colors[j % colors.length];

        page.drawRectangle(null, color, rect);
        page.drawRectangle(GUtil.cutPen, null, rect);

        const top = new PdfRect(rect.x, rect.y, rect.width, rect.height / 2);
        const top1 = top.inflate(-margin2);
        page.drawRoundedRectangle(null, PdfBrush.White, top1, margin2);
        page.drawStringsMulti(
          fixGenArray(q.texts),
          questionFont,
          PdfBrush.Black,
          null,
          top1,
          ETextAlign.Center
        );

        const n = q.answers.length;
        const p = (rect.width - (n + 1) * margin2) / n;
        for (let k = 0; k < n; k++) {
          const r = new PdfRect(
            rect.x + margin2 + k * (p + margin2),
            top.bottom + margin2 / 2,
            p,
            top.height - margin2 * 1.5
          );
          page.drawRoundedRectangle(null, PdfBrush.White, r, margin2);
          page.save();
          page.rotateAtTransform(270, new PdfPoint(r.centerX, r.centerY));
          page.drawFillStringCenter(q.answers[k], GUtil.baseFont, PdfBrush.Black, r);
          page.restore();
        }
      }
    }
  }
}

/**
 * Kolickovani__Side (Peg Board - Vertical Strip)
 * Layout: Portrait pages, 2 columns of 5 strips. Answers on the right edge.
 */
export class Kolickovani__Side extends KolickovaniBase {
  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const countX = 2;
    const countY = 5;
    const itemsPerPage = countX * countY;
    const totalCount = this.countDefault();
    const { origin, size } = calcOrigin(countX, countY, margin, PdfSize.A4);

    for (let i = 0; i < totalCount; i += itemsPerPage) {
      const page = pdf.addPage();
      const count = Math.min(itemsPerPage, totalCount - i);
      const gen = genMulti(opts.data, opts.generator, count, false, false);

      const answerWidth = PdfUnit.FromMillimeters(25);
      const allTexts = gen.map((g) => fixGenArray(g.texts));
      const questionFont = page.adjustFontMulti(
        allTexts,
        GUtil.baseFont,
        new PdfSize(size.width - answerWidth, size.height)
      );

      for (let j = 0; j < gen.length; j++) {
        const x = j % countX;
        const y = Math.floor(j / countX);
        const q = gen[j];
        const rect = new PdfRect(
          origin.x + x * size.width,
          origin.y + y * size.height,
          size.width,
          size.height
        );

        const qRect = new PdfRect(rect.x, rect.y, rect.width - answerWidth, rect.height);
        page.drawRectangle(GUtil.basePen, null, rect);
        page.drawStringsMulti(
          fixGenArray(q.texts),
          questionFont,
          PdfBrush.Black,
          null,
          qRect.inflate(-PdfUnit.FromMillimeters(2)),
          ETextAlign.CenterLeft
        );

        const rowH = rect.height / q.answers.length;
        const answerFont = page.adjustFont(
          answerWidth - PdfUnit.FromMillimeters(2),
          q.answers,
          GUtil.baseFont
        );
        for (let k = 0; k < q.answers.length; k++) {
          const r = new PdfRect(qRect.right, rect.y + k * rowH, answerWidth, rowH);
          page.drawRectangle(GUtil.basePen, null, r);
          page.drawString(q.answers[k], answerFont, PdfBrush.Black, null, r, ETextAlign.Center);
        }
      }
    }
  }
}

/**
 * Kolickovani__Corners (Peg Board - Square Choice)
 * Layout: Portrait pages, 2x2 grid of large squares. Answers in 4 corners.
 */
export class Kolickovani__Corners extends KolickovaniBase {
  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(15);
    const countX = 2;
    const countY = 2;
    const itemsPerPage = countX * countY;
    const totalCount = this.countDefault();
    const { origin, size } = calcOrigin(countX, countY, margin, PdfSize.A4);

    for (let i = 0; i < totalCount; i += itemsPerPage) {
      const page = pdf.addPage();
      const count = Math.min(itemsPerPage, totalCount - i);
      // Force 4 options for corner variant
      const gen = genMulti(opts.data, opts.generator, count, false, false).map((g) => {
        while (g.answers.length < 4) g.answers.push('?');
        if (g.answers.length > 4) g.answers = g.answers.slice(0, 4);
        return g;
      });

      const cornerSize = size.width / 4;
      const allTexts = gen.map((g) => fixGenArray(g.texts));
      const questionFont = page.adjustFontMulti(
        allTexts,
        GUtil.baseFont,
        new PdfSize(size.width - 2 * cornerSize, size.height - 2 * cornerSize)
      );

      for (let j = 0; j < gen.length; j++) {
        const x = j % countX;
        const y = Math.floor(j / countX);
        const q = gen[j];
        const rect = new PdfRect(
          origin.x + x * size.width,
          origin.y + y * size.height,
          size.width,
          size.height
        );

        page.drawRectangle(GUtil.basePen, null, rect);

        const positions = [
          new PdfRect(rect.x, rect.y, cornerSize, cornerSize),
          new PdfRect(rect.right - cornerSize, rect.y, cornerSize, cornerSize),
          new PdfRect(rect.x, rect.bottom - cornerSize, cornerSize, cornerSize),
          new PdfRect(rect.right - cornerSize, rect.bottom - cornerSize, cornerSize, cornerSize),
        ];

        const answerFont = page.adjustFont(cornerSize, q.answers, GUtil.baseFont);
        for (let k = 0; k < 4; k++) {
          page.drawRectangle(GUtil.basePen, null, positions[k]);
          page.drawString(
            q.answers[k],
            answerFont,
            PdfBrush.Black,
            null,
            positions[k],
            ETextAlign.Center
          );
        }

        const qRect = rect.inflate(-cornerSize);
        page.drawStringsMulti(
          fixGenArray(q.texts),
          questionFont,
          PdfBrush.Black,
          null,
          qRect,
          ETextAlign.Center
        );
      }
    }
  }
}

/**
 * Kolickovani__Circles (Vibrant Palette Variant)
 * Layout: 2x3 grid. Each card has a unique bold color.
 */
export class Kolickovani__Circles extends KolickovaniBase {
  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const countX = 2;
    const countY = 3;
    const itemsPerPage = countX * countY;
    const totalCount = this.countDefault();
    const { origin, size } = calcOrigin(countX, countY, margin, PdfSize.A4);
    const rnd = opts.data.random;

    const palettes = GUtil.behackaColorPalettes;

    for (let i = 0; i < totalCount; i += itemsPerPage) {
      const page = pdf.addPage();
      const count = Math.min(itemsPerPage, totalCount - i);
      const gen = genMulti(opts.data, opts.generator, count, false, false);

      const allTexts = gen.map((g) => fixGenArray(g.texts));
      const questionFont = page.adjustFontMulti(
        allTexts,
        GUtil.baseFont,
        new PdfSize(size.width * 0.8, size.height * 0.5)
      );

      for (let j = 0; j < gen.length; j++) {
        const x = j % countX;
        const y = Math.floor(j / countX);
        const q = gen[j];
        const rect = new PdfRect(
          origin.x + x * size.width,
          origin.y + y * size.height,
          size.width,
          size.height
        );

        const palette = palettes[rnd.next(0, palettes.length)].map(
          (h) => new PdfBrush(PdfColor.fromHex(h))
        );
        const cardColor = palette[0];

        page.drawRoundedRectangle(null, cardColor, rect, PdfUnit.FromMillimeters(5));
        page.drawRectangle(GUtil.cutPen, null, rect);

        const qRect = new PdfRect(
          rect.x + margin / 2,
          rect.y + margin / 2,
          rect.width - margin,
          rect.height * 0.6
        );
        page.drawRoundedRectangle(null, PdfBrush.White, qRect, PdfUnit.FromMillimeters(3));
        page.drawStringsMulti(
          fixGenArray(q.texts),
          questionFont,
          PdfBrush.Black,
          null,
          qRect,
          ETextAlign.Center
        );

        const n = q.answers.length;
        const circleD = Math.min(rect.width / n - margin / 4, rect.height * 0.3);
        const circleY = qRect.bottom + (rect.bottom - qRect.bottom - circleD) / 2;
        const startX = rect.x + (rect.width - n * circleD - ((n - 1) * margin) / 4) / 2;

        const answerFont = page.adjustFont(circleD * 0.8, q.answers, GUtil.baseFont);

        for (let k = 0; k < n; k++) {
          const cRect = new PdfRect(startX + k * (circleD + margin / 4), circleY, circleD, circleD);
          const petalColor = palette[(k + 1) % palette.length];

          page.drawEllipse(null, petalColor, cRect);
          page.drawEllipse(GUtil.basePen, null, cRect);
          page.drawString(q.answers[k], answerFont, PdfBrush.Black, null, cRect, ETextAlign.Center);
        }
      }
    }
  }
}

/**
 * Kolickovani__Flowers (Radial Petals Variant)
 * Layout: 2x2 grid of large flowers. Question in center, answers in colorful petals.
 */
export class Kolickovani__Flowers extends KolickovaniBase {
  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const countX = 2;
    const countY = 2;
    const itemsPerPage = countX * countY;
    const totalCount = this.countDefault();
    const { origin, size } = calcOrigin(countX, countY, margin, PdfSize.A4);

    for (let i = 0; i < totalCount; i += itemsPerPage) {
      const page = pdf.addPage();
      const count = Math.min(itemsPerPage, totalCount - i);
      const gen = genMulti(opts.data, opts.generator, count, false, false);

      for (let j = 0; j < gen.length; j++) {
        const x = j % countX;
        const y = Math.floor(j / countX);
        const q = gen[j];
        const rect = new PdfRect(
          origin.x + x * size.width,
          origin.y + y * size.height,
          size.width,
          size.height
        );

        page.drawRectangle(GUtil.cutPen, null, rect);

        const center = new PdfPoint(rect.centerX, rect.centerY);
        const qSize = Math.min(size.width, size.height) * 0.4;
        const qRect = PdfRect.circle(center, qSize);

        const n = q.answers.length;
        const petalRadius = qSize * 0.8;
        const petalSize = qSize * 0.6;

        const answerFont = page.adjustFont(petalSize * 0.8, q.answers, GUtil.baseFont);

        for (let k = 0; k < n; k++) {
          const angle = (k / n) * Math.PI * 2 - Math.PI / 2;
          const px = center.x + Math.cos(angle) * petalRadius;
          const py = center.y + Math.sin(angle) * petalRadius;
          const pRect = PdfRect.circle(new PdfPoint(px, py), petalSize);

          const color = GUtil.standardColors[k % GUtil.standardColors.length];
          page.drawEllipse(null, color, pRect);
          page.drawEllipse(GUtil.basePen, null, pRect);
          page.drawString(q.answers[k], answerFont, PdfBrush.Black, null, pRect, ETextAlign.Center);
        }

        page.drawEllipse(GUtil.basePen, PdfBrush.White, qRect);
        page.drawStringsMulti(
          fixGenArray(q.texts),
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          qRect.inflate(-5),
          ETextAlign.Center
        );
      }
    }
  }
}

/**
 * Kolickovani__Wheel (Radial Sectors Variant)
 * Layout: 2x3 grid of large wheels. Question in center circle, answers in outer annular sectors.
 * Optimized for circular cutting and ink-saving (outline only).
 */
export class Kolickovani__Wheel extends KolickovaniBase {
  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const countX = 2;
    const countY = 3;
    const itemsPerPage = countX * countY;
    const totalCount = this.countDefault();
    const { origin, size } = calcOrigin(countX, countY, margin, PdfSize.A4);

    for (let i = 0; i < totalCount; i += itemsPerPage) {
      const page = pdf.addPage();
      const count = Math.min(itemsPerPage, totalCount - i);
      const gen = genMulti(opts.data, opts.generator, count, false, false);

      for (let j = 0; j < gen.length; j++) {
        const x = j % countX;
        const y = Math.floor(j / countX);
        const q = gen[j];
        const rect = new PdfRect(
          origin.x + x * size.width,
          origin.y + y * size.height,
          size.width,
          size.height
        );

        const center = new PdfPoint(rect.centerX, rect.centerY);
        const outerRadius = Math.min(size.width, size.height) / 2 - PdfUnit.FromMillimeters(2);
        const innerRadiusRatio = 0.6; // Bigger inner circle
        const innerRadius = outerRadius * innerRadiusRatio;

        const wheelRect = PdfRect.circle(center, outerRadius * 2);
        const qRect = PdfRect.circle(center, innerRadius * 2);

        // Circular cut line
        page.drawEllipse(GUtil.cutPen, null, wheelRect);

        const n = q.answers.length;
        const angleStep = 360 / n;
        const alpha = Math.PI / n;

        // Font sizing using KoloLosovani-style logic
        // r_text is distance to the outer edge of the text area
        const r_text = outerRadius - PdfUnit.FromMillimeters(2);
        const textHeight = (outerRadius - innerRadius) * 0.6;
        const textSize = 2 * (r_text - textHeight) * Math.tan(alpha);

        const answerFont = page.adjustFont(textSize, q.answers, GUtil.baseFont);
        const qTexts = fixGenArray(q.texts);
        const qFont = page.adjustFont(innerRadius * 1.6, qTexts, GUtil.baseFont);

        for (let k = 0; k < n; k++) {
          const startAngle = k * angleStep - 90;
          const endAngle = (k + 1) * angleStep - 90;

          // Outer sectors - outline only
          page.drawAnnularSector(
            GUtil.basePen,
            null,
            wheelRect,
            innerRadiusRatio,
            startAngle,
            endAngle
          );

          const midAngle = k * angleStep + angleStep / 2;

          // Rotate coordinates to draw text radially
          page.save();
          // Add 180 to flip the orientation
          page.rotateAtTransform(midAngle + 180, center);

          // Position text at the bottom of the rotated coordinate system (which is visually the same sector)
          const textRect = new PdfRect(
            center.x - textSize / 2,
            center.y + r_text - textHeight,
            textSize,
            textHeight
          );

          page.drawString(
            q.answers[k],
            answerFont,
            PdfBrush.Black,
            null,
            textRect,
            ETextAlign.Center
          );

          page.restore();
        }

        // Center question circle - outline only
        page.drawEllipse(GUtil.basePen, null, qRect);
        page.drawStringsMulti(
          qTexts,
          qFont,
          PdfBrush.Black,
          null,
          qRect.inflate(-PdfUnit.FromMillimeters(2)),
          ETextAlign.Center
        );
      }
    }
  }
}

/**
 * Kolickovani__Rainbow (Rainbow Strip Variant)
 * Layout: 1 column of 6 full-width horizontal strips per page. Beautiful rainbow gradient background.
 * Optimized for cutting: strips are placed directly next to each other with shared cutting lines.
 */
export class Kolickovani__Rainbow extends KolickovaniBase {
  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const pageMargin = PdfUnit.FromMillimeters(10);
    const countX = 1;
    const countY = 6;
    const itemsPerPage = countX * countY;
    const totalCount = this.countDefault();

    const cardWidth = PdfSize.A4.width - 2 * pageMargin;
    const cardHeight = (PdfSize.A4.height - 2 * pageMargin) / countY;

    for (let i = 0; i < totalCount; i += itemsPerPage) {
      const page = pdf.addPage();
      const count = Math.min(itemsPerPage, totalCount - i);
      const gen = genMulti(opts.data, opts.generator, count, false, false);

      const qWidth = cardWidth * 0.35;
      const allTexts = gen.map((g) => fixGenArray(g.texts));
      const questionFont = page.adjustFontMulti(
        allTexts,
        GUtil.baseFont,
        new PdfSize(qWidth - PdfUnit.FromMillimeters(4), cardHeight - PdfUnit.FromMillimeters(4))
      );

      for (let j = 0; j < gen.length; j++) {
        const y = j;
        const q = gen[j];
        const cardRect = new PdfRect(
          pageMargin,
          pageMargin + y * cardHeight,
          cardWidth,
          cardHeight
        );

        // Draw rainbow background
        drawRainbowRect(page, cardRect);

        // Question box
        const qRect = new PdfRect(
          cardRect.x + PdfUnit.FromMillimeters(2),
          cardRect.y + PdfUnit.FromMillimeters(2),
          qWidth,
          cardRect.height - PdfUnit.FromMillimeters(4)
        );
        page.drawRoundedRectangle(GUtil.basePen, PdfBrush.White, qRect, PdfUnit.FromMillimeters(3));
        page.drawStringsMulti(
          fixGenArray(q.texts),
          questionFont,
          PdfBrush.Black,
          null,
          qRect,
          ETextAlign.Center
        );

        // Answer options
        const n = q.answers.length;
        const availableW = cardRect.width - qWidth - PdfUnit.FromMillimeters(6);
        const aWidth = Math.min(
          availableW / n - PdfUnit.FromMillimeters(2),
          cardHeight - PdfUnit.FromMillimeters(4)
        );
        const startX =
          qRect.right +
          PdfUnit.FromMillimeters(2) +
          (availableW - n * aWidth - (n - 1) * PdfUnit.FromMillimeters(2)) / 2;
        const aY = cardRect.centerY - aWidth / 2;

        const answerFont = page.adjustFont(aWidth * 0.8, q.answers, GUtil.baseFont);

        for (let k = 0; k < n; k++) {
          const aRect = new PdfRect(
            startX + k * (aWidth + PdfUnit.FromMillimeters(2)),
            aY,
            aWidth,
            aWidth
          );
          page.drawEllipse(GUtil.basePen, PdfBrush.White, aRect);
          page.drawString(q.answers[k], answerFont, PdfBrush.Black, null, aRect, ETextAlign.Center);
        }
      }

      // Draw shared cutting grid
      for (let x = 0; x <= countX; x++) {
        const px = pageMargin + x * cardWidth;
        page.drawLine(
          GUtil.cutPen,
          new PdfPoint(px, pageMargin),
          new PdfPoint(px, PdfSize.A4.height - pageMargin)
        );
      }
      for (let y = 0; y <= countY; y++) {
        const py = pageMargin + y * cardHeight;
        page.drawLine(
          GUtil.cutPen,
          new PdfPoint(pageMargin, py),
          new PdfPoint(PdfSize.A4.width - pageMargin, py)
        );
      }
    }
  }
}

/**
 * Kolickovani__BW (Black & White Strip Variant)
 * Layout: 1 column of 6 full-width horizontal strips per page. Printer-friendly B&W version.
 * Optimized for cutting: strips are placed directly next to each other with shared cutting lines.
 */
export class Kolickovani__BW extends KolickovaniBase {
  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const pageMargin = PdfUnit.FromMillimeters(10);
    const countX = 1;
    const countY = 6;
    const itemsPerPage = countX * countY;
    const totalCount = this.countDefault();

    const cardWidth = PdfSize.A4.width - 2 * pageMargin;
    const cardHeight = (PdfSize.A4.height - 2 * pageMargin) / countY;

    for (let i = 0; i < totalCount; i += itemsPerPage) {
      const page = pdf.addPage();
      const count = Math.min(itemsPerPage, totalCount - i);
      const gen = genMulti(opts.data, opts.generator, count, false, false);

      const qWidth = cardWidth * 0.35;
      const allTexts = gen.map((g) => fixGenArray(g.texts));
      const questionFont = page.adjustFontMulti(
        allTexts,
        GUtil.baseFont,
        new PdfSize(qWidth - PdfUnit.FromMillimeters(4), cardHeight - PdfUnit.FromMillimeters(4))
      );

      for (let j = 0; j < gen.length; j++) {
        const y = j;
        const q = gen[j];
        const cardRect = new PdfRect(
          pageMargin,
          pageMargin + y * cardHeight,
          cardWidth,
          cardHeight
        );

        // Question box
        const qRect = new PdfRect(
          cardRect.x + PdfUnit.FromMillimeters(2),
          cardRect.y + PdfUnit.FromMillimeters(2),
          qWidth,
          cardRect.height - PdfUnit.FromMillimeters(4)
        );
        page.drawRoundedRectangle(GUtil.basePen, null, qRect, PdfUnit.FromMillimeters(3));
        page.drawStringsMulti(
          fixGenArray(q.texts),
          questionFont,
          PdfBrush.Black,
          null,
          qRect,
          ETextAlign.Center
        );

        // Answer options
        const n = q.answers.length;
        const availableW = cardRect.width - qWidth - PdfUnit.FromMillimeters(6);
        const aWidth = Math.min(
          availableW / n - PdfUnit.FromMillimeters(2),
          cardHeight - PdfUnit.FromMillimeters(4)
        );
        const startX =
          qRect.right +
          PdfUnit.FromMillimeters(2) +
          (availableW - n * aWidth - (n - 1) * PdfUnit.FromMillimeters(2)) / 2;
        const aY = cardRect.centerY - aWidth / 2;

        const answerFont = page.adjustFont(aWidth * 0.8, q.answers, GUtil.baseFont);

        for (let k = 0; k < n; k++) {
          const aRect = new PdfRect(
            startX + k * (aWidth + PdfUnit.FromMillimeters(2)),
            aY,
            aWidth,
            aWidth
          );
          page.drawEllipse(GUtil.basePen, null, aRect);
          page.drawString(q.answers[k], answerFont, PdfBrush.Black, null, aRect, ETextAlign.Center);
        }
      }

      // Draw shared cutting grid
      for (let x = 0; x <= countX; x++) {
        const px = pageMargin + x * cardWidth;
        page.drawLine(
          GUtil.cutPen,
          new PdfPoint(px, pageMargin),
          new PdfPoint(px, PdfSize.A4.height - pageMargin)
        );
      }
      for (let y = 0; y <= countY; y++) {
        const py = pageMargin + y * cardHeight;
        page.drawLine(
          GUtil.cutPen,
          new PdfPoint(pageMargin, py),
          new PdfPoint(PdfSize.A4.width - pageMargin, py)
        );
      }
    }
  }
}
